import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateCampaignPayload,
  UpdateCampaignPayload,
  createCampaign,
  deleteCampaign,
  getCampaign,
  listCampaigns,
  updateCampaign,
} from '~/services/AccommodationService';

export const campaignQueryKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignQueryKeys.all, 'list'] as const,
  list: (filters: { limit?: number; page?: number }) => [...campaignQueryKeys.lists(), filters] as const,
  details: () => [...campaignQueryKeys.all, 'detail'] as const,
  detail: (campaignId: string) => [...campaignQueryKeys.details(), campaignId] as const,
};

export function useGetAllCampaigns({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: campaignQueryKeys.list({ limit: 1000, page: 1 }),
    queryFn: async ({ signal }) => {
      const response = await listCampaigns({ limit: 1000, page: 1, signal });
      return response.result;
    },
    enabled,
  });
}

interface UseGetCampaignParams {
  campaignId: string;
  enabled?: boolean;
}

export function useGetCampaign({ campaignId, enabled = true }: UseGetCampaignParams) {
  return useQuery({
    queryKey: campaignQueryKeys.detail(campaignId),
    queryFn: async ({ signal }) => {
      const response = await getCampaign({ campaignId, signal });
      return response.result;
    },
    enabled: enabled && !!campaignId,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ payload }: { payload: CreateCampaignPayload }) => {
      const response = await createCampaign({ payload });
      return response.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.lists() });
      enqueueSnackbar('Campaign created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create campaign: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, payload }: { campaignId: string; payload: UpdateCampaignPayload }) => {
      return updateCampaign({ campaignId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.detail(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.lists() });
      enqueueSnackbar('Campaign updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update campaign: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId }: { campaignId: string }) => {
      return deleteCampaign({ campaignId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.detail(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.lists() });
      enqueueSnackbar('Campaign deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete campaign: ${error.message}`, { variant: 'error' });
    },
  });
}
